import { Injectable } from '@angular/core';
import { CognitoService } from './cognito.service';
import { Router } from '@angular/router';
import { Account, User, Role } from '../models/AccountModel';
import { Session } from '../models/SessionModel';
import { PlatformApiService } from './platform-api.service';
import { forkJoin, of as observableOf } from 'rxjs';  // change to new RxJS 6 import syntax
import { map, take, catchError } from 'rxjs/operators';
import * as AWS from 'aws-sdk/global';
import { environment } from '../../environments/environment';
import * as moment from 'moment';
import { OidcFactory, AuthenticationType } from 'src/app/services/oidc.factory';
import { Notification, NotificationType } from '../models/NotificationModel';
import { LoggerService } from './logger.service';
import { NotificationsComponent } from '../secured/account/notifications.component';

@Injectable()
export class SessionService {

  constructor(public cognitoService: CognitoService, public platformApi: PlatformApiService, public _router: Router, private _oidcFactory: OidcFactory) { }
  static roles: Role[];
  static environmentNotificationLabel?: string;
  static environmentNotificationDesc?: string;
  static overrideSessionTimeoutAction: ()=>void;
  private currentSession: Session = new Session();
  private notifications: Notification[];

  static initialize(platformApiService:PlatformApiService, loggerService:LoggerService) {
    console.log('Initializing the session service.');

    SessionService.environmentNotificationLabel = environment.environmentNotificationLabel;
    SessionService.environmentNotificationDesc = environment.environmentNotificationDesc;

    // Not currently using roles
    // return forkJoin([
    //   platformApiService.getRoles()
    // ]).pipe(map(t => {
    //   SessionService.roles = t[0];
    //   console.log(SessionService.environmentNotificationLabel);
    //   return;
    // }),catchError((err) => {
    //   console.log(err);
    //   return observableOf([]);
    // }));

    return observableOf([])
  }

  public refreshSessionData() {
    if (!this.isAccountType('SYSTEM') && this.getCurrentAccount()) {
      this.platformApi.getNotifications(this.getCurrentAccount().id).pipe(take(1)).subscribe(result => {
        this.notifications = result.results;
      })
    }
  }

  public setLoginRedirect(redirectUrl) {
    localStorage.setItem('loginRedirect', redirectUrl);
  }

  public doLoginRedirect(loginRedirect, useStoredRedirect:boolean = true) {
    let appliedRedirect;

    if (useStoredRedirect) {
      appliedRedirect = localStorage.getItem('loginRedirect') || loginRedirect;
    } else {
      appliedRedirect = loginRedirect;
    }

    this._router.navigate([appliedRedirect]);
    localStorage.removeItem('loginRedirect');
  }

  public getNotifications() {
    if (this.notifications && !this.getCurrentUser().getPreference('notifications.marketing.inApp').getBooleanValue()) {
      return this.notifications.filter(el => el.type != NotificationType.MARKETING)
    } else {
      return this.notifications;
    }
  }

  public countUnreadNotifications():number {
    const notifications = this.getNotifications();
    if (notifications) {
      return notifications.map((el) => {
        return el.isRead ? 0 : 1;
      }).reduce((a, b) => a + b, 0);
    } else {
      return 0;
    }
  }

  private logoutCognito() {
    if (this.cognitoService.getCurrentUser()) {
      this.cognitoService.getCurrentUser().signOut();
    }
    if (AWS.config.credentials) {
      (AWS.config.credentials as AWS.CognitoIdentityCredentials).clearCachedId();
    }
  }

  private logoutSSO() {
    const authType = this.getAuthenticationType();
    if(this._oidcFactory.isOidcService(authType)) {
      const oidcService = this._oidcFactory.getOidcService(authType);
      oidcService.getManager().removeUser();
    }
  }

  private isValidCognitoSession():Promise<boolean> {
    return new Promise((resolve, reject) => {
      const cognitoUser = this.cognitoService.getCurrentUser();
      if (cognitoUser != null) {
        cognitoUser.getSession((err, session) => {
          if (err) {
            console.error('SessionService: Couldn\'t get the session: ' + err, err.stack);
            resolve(false);
          } else {
            // console.log("SessionService: valid login");
            resolve(session.isValid());
          }
        });
      } else {
        console.log('SessionService: cognitoUser is null');
        resolve(false);
      }
    });
  }

  private isValidSsoSession(authType: AuthenticationType):Promise<boolean> {
    return new Promise((resolve, reject) => {
      const oidcService = this._oidcFactory.getOidcService(authType);
      oidcService.getManager().getUser().then(user => {
        if (user) {
          resolve(!user.expired);
        } else {
          resolve(false);
        }
      })
    });
  }

  private isValidManageAuthentication():Promise<boolean> {
    return new Promise((resolve, reject) => {
      const authCreds = this.getAuthenticationCredentials();
      resolve(authCreds.companyid && authCreds.memberid && authCreds.memberHash);
    })
  }

  setAuthenticationType(authType: AuthenticationType, authCredentials?) {
    localStorage.setItem('authenticationType', authType);
    if (authCredentials) {
      localStorage.setItem('authenticationCredentials', JSON.stringify(authCredentials));
    } else {
      localStorage.removeItem('authenticationCredentials');
    }
  }

  getAuthenticationType():AuthenticationType {
    return localStorage.getItem('authenticationType') as AuthenticationType;
  }

  getAuthenticationCredentials() {
    const authenticationCredentials = localStorage.getItem('authenticationCredentials');
    if (authenticationCredentials) {
      return JSON.parse(authenticationCredentials);
    } else {
      return undefined;
    }
  }

  logout() {
    this.logoutCognito();
    this.logoutSSO();
    localStorage.removeItem('availableCredentials');
    localStorage.removeItem('workingCredential');
    localStorage.removeItem('sessionTimestamp');
    localStorage.removeItem('authenticationType');
    localStorage.removeItem('authenticationCredentials');
  }

  isAuthenticated():Promise<boolean> {
    const authType = this.getAuthenticationType();

    if (authType == AuthenticationType.COGNITO) {
      return this.isValidCognitoSession();
    } else if (authType == AuthenticationType.SSOJWT || authType == AuthenticationType.SBAM) {
      return this.isValidSsoSession(authType);
    } else if (authType == AuthenticationType.CONNECTWISE) {
      return this.isValidManageAuthentication();
    } else {
      return Promise.resolve(false);
    }
  }

  setApiCredentials(credentials:{user:User, account:Account}[]) {
    const sortedCreds = credentials;

    sortedCreds.sort((a, b) => {
      return a.account.name.localeCompare(b.account.name);
    })

    localStorage.setItem('availableCredentials', JSON.stringify(credentials));
    localStorage.removeItem('workingCredential');

    if (credentials.length == 1) {
      this.setWorkingCredentials(0);
    }
  }

  getApiCredentials(): Array<CredentialData> {
    const apiCredentials = localStorage.getItem('availableCredentials');
    if (apiCredentials) {
      return JSON.parse(apiCredentials);
    } else {
      return undefined;
    }
  }

  getApiRequestHeaders():Promise<{}> {
    const authType = this.getAuthenticationType();
    const authCreds = this.getAuthenticationCredentials();

    if (authType == AuthenticationType.COGNITO) {
      return this.cognitoService.getIdToken().then(idToken => {
        if (idToken) {
          return {
              ['Authorization']: `Bearer ${idToken}`
            };
        } else return {};
      }).catch(err => {
        return {};
      })
    } else if (authType == AuthenticationType.SSOJWT || authType == AuthenticationType.SBAM) {
      return Promise.resolve({
        ['Authorization']: `Bearer ${this.getAuthenticationCredentials().accessToken ? this.getAuthenticationCredentials().accessToken : this.getAuthenticationCredentials().idToken}`
      });
    } else if (authType == AuthenticationType.CONNECTWISE && authCreds) {
      const site = authCreds.site.replace('https://', '').split('/')[0].toLowerCase();
      let headerObj;
      if (authCreds.memberContext) {
        headerObj = {
          ['x-connectwise-companyid']: authCreds.companyid,
          ['x-connectwise-memberid']: authCreds.memberid,
          ['x-connectwise-memberhash']: authCreds.memberHash,
          ['x-connectwise-site']: site,
          ['x-connectwise-membercontext']: authCreds.memberContext
        }
      } else {
        headerObj = {
          ['x-connectwise-companyid']: authCreds.companyid,
          ['x-connectwise-memberid']: authCreds.memberid,
          ['x-connectwise-memberhash']: authCreds.memberHash,
          ['x-connectwise-site']: site
        }
      }
      return Promise.resolve(headerObj);
    } else {
      console.log('No auth headers');
      return Promise.resolve({});
    }
  }

  getSession():Session {
    return this.currentSession;
  }

  setWorkingCredentials(idx:number) {
    const availableCredentials = this.getApiCredentials();
    localStorage.setItem('workingCredential', JSON.stringify(availableCredentials[idx]));
  }

  getWorkingCredential(): CredentialData {
    const workingCredentialStr = localStorage.getItem('workingCredential')
    if (workingCredentialStr) {
      const workingCredentialJson = JSON.parse(workingCredentialStr);
      const workingCredential = {
        user: new User(workingCredentialJson.user),
        account: new Account(workingCredentialJson.account),
      }
      return workingCredential;
    } else {
      return undefined;
    }
  }

  updateWorkingAccount(account: Account) {
    const availableCredentials: Array<CredentialData> = this.getApiCredentials();
    if (!availableCredentials)
      return;

    const inx = availableCredentials.findIndex(cred => cred.account.id === account.id);
    if (inx === -1)
      return;

    availableCredentials[inx].account = account;
    localStorage.setItem('availableCredentials', JSON.stringify(availableCredentials));

    if (this.getWorkingCredential().account.id === account.id) {
      this.setWorkingCredentials(inx);
    }
  }

  updateWorkingUser(user: User) {
    const availableCredentials: Array<CredentialData> = this.getApiCredentials();
    if (!availableCredentials)
      return;

    const inx = availableCredentials.findIndex(cred => cred.user.id === user.id);
    if (inx === -1)
      return;

    availableCredentials[inx].user = user;
    localStorage.setItem('availableCredentials', JSON.stringify(availableCredentials));

    if (this.getWorkingCredential().user.id === user.id) {
      this.setWorkingCredentials(inx);
    }
  }

  getCurrentUser(): User {
    const workingCredential = this.getWorkingCredential();
    if (workingCredential) {
      return workingCredential.user;
    } else {
      return undefined;
    }
  }

  getCurrentAccount(): Account {
    const workingCredential = this.getWorkingCredential();
    if (workingCredential) {
      return workingCredential.account;
    } else {
      return undefined;
    }
  }

  updateCurrentUserPreferences(preferences) {
    const cUser = this.getCurrentUser();
    cUser.preferences = preferences;
    this.updateWorkingUser(cUser);
  }

  setCurrentUserPreference(preferenceKey, newValue) {
    const cUser = this.getCurrentUser();

    const payload = [];
    for (const pref of cUser.preferences) {
      if (pref.value != undefined && pref.key != preferenceKey) {
        payload.push({
          preferenceId: pref.preferenceId,
          key: pref.key,
          value: pref.value
        })
      } else if (pref.key == preferenceKey) {
        payload.push({
          preferenceId: pref.preferenceId,
          key: pref.key,
          value: newValue
        })
      }
    }

    this.platformApi.setUserPreferences(this.getCurrentAccount().id, cUser.id, {preferences: payload}).subscribe((newPrefs) => {
      this.updateCurrentUserPreferences(newPrefs);
    }, err => {
      console.log('Failed to save user preferences', err);
    })
  }

  handleSessionTimeout() {
    if(SessionService.overrideSessionTimeoutAction) {
      SessionService.overrideSessionTimeoutAction();
    } else {
      this._router.navigate(['/public/logout'], { queryParams: { timeout: true } });
    }
  }

  hasRole(role:string):boolean {
    const curUser = this.getCurrentUser();
    if (curUser.roles) {
      return (this.getCurrentUser().roles.find(rl => rl.name == role) != undefined);
    } else {
      return false;
    }
  }

  isSubAccount(): boolean {
    return (this.getCurrentAccount().parentId != undefined);
  }

  isInTrial(): boolean {
    if (this.isAccountType('SYSTEM')) {
      return false;
    } else {
      const planSub = (this.getCurrentAccount().parentId == undefined) ? this.getCurrentAccount().planSubscription : this.getCurrentAccount().masterPlanSubscription;

      if (planSub && planSub.trialPeriodEndDate && planSub.paymentStatus == 'trial') {
        return moment(planSub.trialPeriodEndDate).isAfter(moment());
      } else {
        return false;
      }
    }
  }

  isExpiredTrial(): boolean {
    if (this.isAccountType('SYSTEM')) {
      return false;
    } else {
      const planSub = (this.getCurrentAccount().parentId == undefined) ? this.getCurrentAccount().planSubscription : this.getCurrentAccount().masterPlanSubscription;

      if (planSub && planSub.trialPeriodEndDate) {
        return moment(planSub.trialPeriodEndDate).isBefore(moment()) && planSub.paymentStatus == 'trial';
      } else {
        return false;
      }
    }
  }

  getInstanceStatus(): string {
    return this.getCurrentAccount().instanceStatus;
  }

  getPaymentStatus(): string {
    const planSub = (this.getCurrentAccount().parentId == undefined) ? this.getCurrentAccount().planSubscription : this.getCurrentAccount().masterPlanSubscription;

    if (planSub) {
      return planSub.paymentStatus;
    } else {
      return undefined;
    }
  }

  daysLeftInTrial(): number {
    const planSub = (this.getCurrentAccount().parentId == undefined) ? this.getCurrentAccount().planSubscription : this.getCurrentAccount().masterPlanSubscription;

    if (this.isInTrial() && planSub && planSub.trialPeriodEndDate) {
      return Math.ceil(moment(planSub.trialPeriodEndDate).diff(moment(), 'days', true));
    } else {
      return undefined;
    }
  }

  checkAccountFeature(account:Account, feature:string):boolean {
    if (this.isAccountType('SYSTEM')) {
      return (account.type != 'SYSTEM' && account.planSubscription.getFeature(feature));
    } else {
      return this.hasFeature(feature);
    }
  }

  hasFeature(feature:string):boolean {
    const curAccount = this.getCurrentAccount();

    if (curAccount && curAccount.planSubscription && curAccount.planSubscription.features) {
      const acctFeature = curAccount.planSubscription.features.find(el => el.featureType == feature);
      if (acctFeature) {
        let valCheck = acctFeature.value;
        if (this.isInTrial()) {
          valCheck = acctFeature.trialValue;
        }
        if (!valCheck || valCheck == '0' || valCheck == 'false' || valCheck == 'null') {
          return false;
        } else {
          return true;
        }
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  getAccountFeature(account:Account, feature:string):any {
    if (this.isAccountType('SYSTEM')) {
      return account.planSubscription.getFeature(feature);
    } else {
      return this.getFeature(feature);
    }
  }

  getFeature(feature:string):any {
    const curAccount = this.getCurrentAccount();

    if (curAccount && curAccount.planSubscription.features) {
      const acctFeature = curAccount.planSubscription.features.find(el => el.featureType == feature);
      if (acctFeature) {
        console.log(curAccount, acctFeature);
        let valCheck = acctFeature.value;
        if (this.isInTrial()) {
          valCheck = acctFeature.trialValue;
        }
        return valCheck
      } else {
        return undefined;
      }
    } else {
      return undefined;
    }
  }

  isSystemUser() {
    const creds = this.getApiCredentials();

    const sysAcct = creds.find(el => {
      if (el.account.type == 'SYSTEM') {
        return true;
      } else {
        return false;
      }
    })

    if (sysAcct == undefined) {
      return false;
    } else {
      return true;
    }
  }

  isAccountType(accountType:string):boolean {
    const gca = this.getCurrentAccount();
    if (gca) {
      return gca.type == accountType;
    } else {
      return false;
    }
  }

  authorizedRoute(route) {
    return true;
  }

  getRoles(): Role[] {
    return SessionService.roles;
  }

  getEnvNotification(): {label:string,desc:string} {
    return {label:SessionService.environmentNotificationLabel, desc:SessionService.environmentNotificationDesc};
  }
}

export class CredentialData {
  user: User;
  account: Account;
}
